// Übersicht der Smart Views auf eigenen Seiten: /smart-views/alle, /eigene, /geteilt, /andere.
// Liste mit Name, Freigabe, Besitzer, Trefferzahl (aus Close, nacheinander geholt, 5 Min. gemerkt), Suche,
// Anheften/Lösen für die Seitenleiste (nur im CRM). Klick oder Enter öffnet die Smart View, j/k bewegen, p heftet an.
// Runde 5 (Agent 5B): Spalten „Zuletzt geändert“ und „Zuletzt genutzt“ (nur von mir), sichtbarer „…“-Knopf je Zeile mit Öffnen, Link kopieren,
// Umbenennen, Teilen, Anheften, Löschen (Schreiben über `smartViewsSchreibenApi`, Agent 5A), gemerkte Sortierung.
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { fehlerText, type SmartViewKurz } from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { smartViewsSchreibenApi } from "../api/filter";
import { listenAnsichtenApi, type SmartViewZeiten } from "../api/listenAnsichten";
import { useAuth } from "../auth/AuthContext";
import { Avatar, Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Dialog } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { KontextMenue, MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeldung } from "../components/Meldungen";
import { useGemerkteAnsicht } from "../daten/listenAnsichtStore";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { anheften, istTrenner, useSmartViews, useZahlen, zahlenAnfordern } from "../daten/smartviews";
import { T } from "../texte";
import { kopieren, useTasten } from "../werkzeuge/tasten";
import { relativ, zeitpunktLang } from "../werkzeuge/zeit";
import "./SmartViews.css";

export type SmartViewArt = "alle" | "eigene" | "geteilt" | "andere";

const REITER: { art: SmartViewArt; label: string; titel: string }[] = [
  { art: "alle", label: T.smartViews.alle, titel: T.smartViews.alleLang },
  { art: "eigene", label: T.smartViews.eigene, titel: T.smartViews.eigeneLang },
  { art: "geteilt", label: T.smartViews.geteilt, titel: T.smartViews.geteiltLang },
  { art: "andere", label: T.smartViews.andere, titel: T.smartViews.andereLang },
];

const LEER_TEXT: Record<SmartViewArt, string> = {
  alle: "Es gibt noch keine Smart Views. Sie entstehen in Close und kommen mit dem Abgleich hierher.",
  eigene: "Du hast noch keine eigenen Smart Views.",
  geteilt: "Keine geteilten Smart Views.",
  andere: "Niemand sonst hat Smart Views mit dir geteilt.",
};

type Sortierung = "name" | "besitzer" | "zahl" | "geaendert" | "genutzt";
const SORTIERUNGEN: Sortierung[] = ["name", "besitzer", "zahl", "geaendert", "genutzt"];

/** Name ohne führende Emojis/Zeichen, damit „📅 Simon · …“ und „Simon · …“ beieinander stehen. */
function sortierName(name: string): string {
  return name.replace(/^[^\p{L}\p{N}]+/u, "").toLocaleLowerCase("de");
}

export default function SmartViews({ art }: { art: SmartViewArt }) {
  const reiter = REITER.find((r) => r.art === art) ?? REITER[0];
  useSeitenKopf([{ label: T.smartViews.titel, to: "/smart-views/alle" }, { label: reiter.titel }]);
  const { views, fehler, laedt, neuLaden } = useSmartViews();
  const meta = useMeta();
  const { nutzer } = useAuth();
  const navigate = useNavigate();
  const meldung = useMeldung();
  const zahl = useZahlen();
  const [suche, setSuche] = useState("");
  const [fokus, setFokus] = useState(0);
  const [kontext, setKontext] = useState<{ x: number; y: number; punkte: MenuePunkt[] } | null>(null);
  const [umbenennen, setUmbenennen] = useState<{ view: SmartViewKurz; name: string; laeuft: boolean } | null>(null);
  const [loeschen, setLoeschen] = useState<{ view: SmartViewKurz; laeuft: boolean } | null>(null);
  // Sortierung wird je Nutzer gemerkt (Liste „smartviews“)
  const gemerkt = useGemerkteAnsicht("smartviews", false);
  const gemerktSort = gemerkt.ansicht?.einstellungen.sort;
  const sort: Sortierung = SORTIERUNGEN.includes(gemerktSort as Sortierung) ? (gemerktSort as Sortierung) : "name";
  const setSort = (s: Sortierung) => gemerkt.aendern({ einstellungen: { sort: s === "name" ? null : s } });
  // wann zuletzt geändert, wann zuletzt von mir genutzt
  const [zeiten, setZeiten] = useState<Record<string, SmartViewZeiten>>({});
  const zeitenLaden = useCallback(() => {
    listenAnsichtenApi
      .smartViewZeiten()
      .then((r) => setZeiten(Object.fromEntries(r.data.map((x) => [x.id, x]))))
      .catch(() => undefined);
  }, []);
  useEffect(() => zeitenLaden(), [zeitenLaden]);
  useEchtzeit(["smart_view.updated"], () => zeitenLaden());

  const eigeneIds = useMemo(() => new Set([nutzer?.id, nutzer?.close_user_id].filter(Boolean) as string[]), [nutzer]);
  const istEigen = (v: SmartViewKurz) => !!v.owner_id && eigeneIds.has(v.owner_id);

  const alle = useMemo(() => (views ?? []).filter((v) => !istTrenner(v.name)), [views]);
  const nachArt = useMemo(() => {
    const f: Record<SmartViewArt, (v: SmartViewKurz) => boolean> = {
      alle: () => true,
      eigene: (v) => istEigen(v),
      geteilt: (v) => v.is_shared,
      andere: (v) => !istEigen(v),
    };
    return (a: SmartViewArt) => alle.filter(f[a]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alle, eigeneIds]);

  const n = suche.trim().toLocaleLowerCase("de");
  const zeilen = nachArt(art)
    .filter((v) => !n || v.name.toLocaleLowerCase("de").includes(n) || meta.nutzerName(v.owner_id).toLocaleLowerCase("de").includes(n))
    .sort((a, b) => {
      if (sort === "besitzer") {
        const x = meta.nutzerName(a.owner_id).localeCompare(meta.nutzerName(b.owner_id), "de");
        if (x) return x;
      } else if (sort === "zahl") {
        const za = zahl(a.id)?.total ?? -1;
        const zb = zahl(b.id)?.total ?? -1;
        if (za !== zb) return zb - za;
      } else if (sort === "geaendert" || sort === "genutzt") {
        // neueste zuerst, ohne Angabe zuletzt
        const feld = sort === "geaendert" ? "date_updated" : "zuletzt_genutzt_at";
        const x = zeiten[a.id]?.[feld] ?? "";
        const y = zeiten[b.id]?.[feld] ?? "";
        if (x !== y) return !x ? 1 : !y ? -1 : y.localeCompare(x);
      }
      return sortierName(a.name).localeCompare(sortierName(b.name), "de", { numeric: true });
    });

  // Trefferzahlen der Lead-Views dieser Seite holen (höchstens zwei Anfragen gleichzeitig)
  const zahlIds = zeilen.filter((v) => (v.type || "lead") === "lead").map((v) => v.id).join(",");
  useEffect(() => {
    if (zahlIds) zahlenAnfordern(zahlIds.split(","));
  }, [zahlIds]);

  useEffect(() => setFokus(0), [art, suche]);

  const pinAendern = (v: SmartViewKurz) => {
    anheften(v.id, !v.pinned).catch((e: unknown) => meldung.zeige("fehler", e instanceof Error ? e.message : "Anheften hat nicht geklappt."));
  };

  const oeffnen = (v: SmartViewKurz) => navigate(`/smart-views/${v.id}`);

  useTasten({
    j: () => setFokus((f) => Math.min(f + 1, zeilen.length - 1)),
    ArrowDown: () => setFokus((f) => Math.min(f + 1, zeilen.length - 1)),
    k: () => setFokus((f) => Math.max(f - 1, 0)),
    ArrowUp: () => setFokus((f) => Math.max(f - 1, 0)),
    Enter: () => {
      const v = zeilen[fokus];
      if (v) oeffnen(v);
    },
    p: () => {
      const v = zeilen[fokus];
      if (v) pinAendern(v);
    },
  });

  useEffect(() => {
    document.querySelector(`tr[data-index="${fokus}"]`)?.scrollIntoView({ block: "nearest" });
  }, [fokus]);

  /** Punkte des „…“-Knopfs und des Rechtsklick-Menüs. Umbenennen, Teilen und Löschen gibt es nur für eigene Smart Views. */
  const menuePunkte = (v: SmartViewKurz): MenuePunkt[] => {
    const eigen = istEigen(v);
    const inClose = v.id.startsWith("save_") && !v.id.includes("_crm_");
    return [
      { label: T.smartViews.oeffnen, icon: "pfeil-rechts", onClick: () => oeffnen(v) },
      { label: T.smartViews.neuerTab, icon: "extern", onClick: () => window.open(`/smart-views/${v.id}`, "_blank", "noopener") },
      {
        label: "Link kopieren",
        icon: "kopieren",
        onClick: () =>
          void kopieren(`${window.location.origin}/smart-views/${v.id}`).then((ok) =>
            ok ? meldung.ok("Link kopiert.") : meldung.zeige("fehler", "Der Link ließ sich nicht kopieren."),
          ),
      },
      { label: v.pinned ? "Aus Seitenleiste entfernen" : "In der Seitenleiste anheften", icon: "stecknadel", onClick: () => pinAendern(v) },
      ...(eigen
        ? [
            { label: "Umbenennen", icon: "stift" as const, trenner: true, onClick: () => setUmbenennen({ view: v, name: v.name, laeuft: false }) },
            { label: v.is_shared ? "Nicht mehr teilen" : "Mit allen teilen", icon: "link" as const, onClick: () => teilen(v) },
          ]
        : []),
      ...(inClose ? [{ label: T.smartViews.inClose, icon: "extern" as const, trenner: !eigen, onClick: () => window.open(`https://app.close.com/leads/${v.id}/`, "_blank", "noopener") }] : []),
      ...(eigen ? [{ label: "Löschen …", icon: "papierkorb" as const, gefahr: true, trenner: true, onClick: () => setLoeschen({ view: v, laeuft: false }) }] : []),
    ];
  };
  const menue = (e: MouseEvent, v: SmartViewKurz) => {
    e.preventDefault();
    setKontext({ x: e.clientX, y: e.clientY, punkte: [{ label: v.name, ueberschrift: true }, ...menuePunkte(v)] });
  };

  const teilen = (v: SmartViewKurz) => {
    smartViewsSchreibenApi
      .aendern(v.id, { is_shared: !v.is_shared })
      .then(() => {
        meldung.ok(v.is_shared ? "Wird nicht mehr geteilt." : "Jetzt mit allen geteilt.");
        void neuLaden();
        zeitenLaden();
      })
      .catch((e: unknown) => meldung.zeige("fehler", fehlerText(e)));
  };
  const umbenennenSpeichern = () => {
    if (!umbenennen) return;
    const name = umbenennen.name.trim();
    if (!name || name === umbenennen.view.name) {
      setUmbenennen(null);
      return;
    }
    setUmbenennen({ ...umbenennen, laeuft: true });
    smartViewsSchreibenApi
      .aendern(umbenennen.view.id, { name })
      .then(() => {
        setUmbenennen(null);
        meldung.ok("Umbenannt.");
        void neuLaden();
        zeitenLaden();
      })
      .catch((e: unknown) => {
        setUmbenennen((u) => (u ? { ...u, laeuft: false } : u));
        meldung.zeige("fehler", fehlerText(e));
      });
  };
  const loeschenBestaetigt = () => {
    if (!loeschen) return;
    setLoeschen({ ...loeschen, laeuft: true });
    smartViewsSchreibenApi
      .loeschen(loeschen.view.id)
      .then(() => {
        setLoeschen(null);
        meldung.ok("Smart View gelöscht.");
        void neuLaden();
        zeitenLaden();
      })
      .catch((e: unknown) => {
        setLoeschen((l) => (l ? { ...l, laeuft: false } : l));
        meldung.zeige("fehler", fehlerText(e));
      });
  };

  const angeheftet = alle.filter((v) => v.pinned).length;
  const absteigend = (key: Sortierung) => key === "zahl" || key === "geaendert" || key === "genutzt";
  const kopfSort = (key: Sortierung, label: string, klasse?: string) => (
    <th className={klasse} aria-sort={sort === key ? (absteigend(key) ? "descending" : "ascending") : undefined}>
      <button type="button" className={`tabelle__sort${sort === key ? " tabelle__sort--an" : ""}`} onClick={() => setSort(key)}>
        <span>{label}</span>
        {sort === key ? <Icon name={absteigend(key) ? "pfeil-runter" : "pfeil-hoch"} groesse={12} /> : null}
      </button>
    </th>
  );
  const zeitZelle = (iso: string | null | undefined) =>
    iso ? <span title={zeitpunktLang(iso)}>{relativ(iso)}</span> : <span className="leise">—</span>;

  return (
    <div className="liste-seite">
      <div className="liste-kopf">
        <h1 className="liste-kopf__titel">{T.smartViews.titel}</h1>
        <nav className="reiter reiter--seite" aria-label="Smart-View-Listen">
          {REITER.map((r) => (
            <NavLink key={r.art} to={`/smart-views/${r.art}`} className={({ isActive }) => `reiter__knopf${isActive ? " reiter__knopf--an" : ""}`}>
              {r.label}
              {views ? <span className="reiter__anzahl">{nachArt(r.art).length}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="liste-kopf__rechts">
          <label className="suchfeld suchfeld--kopf">
            <Icon name="suche" groesse={14} />
            <input value={suche} placeholder={T.smartViews.suchen} onChange={(e) => setSuche(e.target.value)} aria-label={T.smartViews.suchen} />
            {suche ? (
              <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Suche leeren" onClick={() => setSuche("")}>
                <Icon name="schliessen" groesse={12} />
              </button>
            ) : null}
          </label>
        </div>
      </div>
      <div className="liste-leiste">
        <span className="liste-leiste__zahl">
          {zeilen.length} {zeilen.length === 1 ? "Smart View" : "Smart Views"}
          {angeheftet ? <span className="leise"> · {angeheftet} angeheftet</span> : null}
        </span>
        <span className="liste-leiste__hinweis leise">{T.smartViews.anheftenHinweis}</span>
        <button type="button" className="knopf knopf--leise knopf--klein liste-leiste__rechts" onClick={() => zahlenAnfordern(zeilen.map((v) => v.id), true)} title="Trefferzahlen neu von Close holen">
          <Icon name="neu-laden" groesse={13} />
          Zahlen neu
        </button>
      </div>
      {fehler && !views ? <FehlerBox fehler={fehler} onNochmal={() => void neuLaden()} /> : null}
      {laedt && !views ? (
        <Laden />
      ) : views && !zeilen.length ? (
        <Leer titel={suche ? T.suche.leer : "Keine Smart Views."} text={suche ? "Suchbegriff ändern." : LEER_TEXT[art]} />
      ) : zeilen.length ? (
        <div className="tabelle-rahmen">
          <table className="tabelle tabelle--views">
            <thead>
              <tr>
                <th className="spalte-pin" aria-label="Angeheftet" />
                {kopfSort("name", "Name", "spalte-name")}
                <th className="spalte-freigabe">
                  <span className="tabelle__kopf">Freigabe</span>
                </th>
                {kopfSort("besitzer", "Besitzer", "spalte-besitzer")}
                {kopfSort("geaendert", "Zuletzt geändert", "spalte-zeit")}
                {kopfSort("genutzt", "Zuletzt genutzt", "spalte-zeit spalte-genutzt")}
                {kopfSort("zahl", "Leads", "spalte-zahl")}
                <th className="spalte-menue" aria-label="Weitere Aktionen" />
              </tr>
            </thead>
            <tbody>
              {zeilen.map((v, i) => {
                const z = zahl(v.id);
                const lead = (v.type || "lead") === "lead";
                return (
                  <tr
                    key={v.id}
                    data-index={i}
                    className={i === fokus ? "tabelle__zeile--fokus" : undefined}
                    aria-selected={i === fokus}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest("button, a")) return;
                      setFokus(i);
                      oeffnen(v);
                    }}
                    onContextMenu={(e) => {
                      setFokus(i);
                      menue(e, v);
                    }}
                  >
                    <td className="spalte-pin">
                      <button
                        type="button"
                        className={`pin${v.pinned ? " pin--an" : ""}`}
                        title={v.pinned ? T.smartViews.loesen : T.smartViews.anheften}
                        aria-label={`${v.pinned ? T.smartViews.loesen : T.smartViews.anheften}: ${v.name}`}
                        aria-pressed={v.pinned}
                        onClick={() => pinAendern(v)}
                      >
                        <Icon name="stecknadel" groesse={14} />
                      </button>
                    </td>
                    <td className="spalte-name">
                      <div className="zelle">
                        <a
                          href={`/smart-views/${v.id}`}
                          className="tabelle__name"
                          title={v.name}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                            e.preventDefault();
                            oeffnen(v);
                          }}
                        >
                          {v.name}
                        </a>
                        {!lead ? <span className="marke marke--leise">{T.smartViews.kontakte}</span> : null}
                      </div>
                    </td>
                    <td className="spalte-freigabe">
                      <span className="freigabe">
                        <Icon name={v.is_shared ? "link" : "schloss"} groesse={13} />
                        {v.is_shared ? T.smartViews.geteiltMarke : T.smartViews.privat}
                      </span>
                    </td>
                    <td className="spalte-besitzer">
                      <span className="besitzer">
                        <Avatar name={meta.nutzerName(v.owner_id)} id={v.owner_id} groesse={18} />
                        <span className="besitzer__name">{meta.nutzerName(v.owner_id)}</span>
                      </span>
                    </td>
                    <td className="spalte-zeit">{zeitZelle(zeiten[v.id]?.date_updated)}</td>
                    <td className="spalte-zeit spalte-genutzt">{zeitZelle(zeiten[v.id]?.zuletzt_genutzt_at)}</td>
                    <td className="spalte-zahl">
                      {!lead ? (
                        <span className="leise">–</span>
                      ) : !z ? (
                        <span className="zahl-laedt" aria-label="Zahl wird geholt" />
                      ) : z.total === null ? (
                        <span className="leise" title={z.fehler ?? "Keine Zahl von Close"}>
                          –
                        </span>
                      ) : (
                        <span title={z.stand ? `Stand ${zeitpunktLang(z.stand)} (${relativ(z.stand)})` : undefined}>{z.total.toLocaleString("de-DE")}</span>
                      )}
                    </td>
                    <td className="spalte-menue">
                      <MenueKnopf knopfKlasse="symbolknopf sv-mehr" titel={`Weitere Aktionen: ${v.name}`} ausrichtung="end" punkte={menuePunkte(v)}>
                        <Icon name="mehr" groesse={16} />
                      </MenueKnopf>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      <KontextMenue ort={kontext} punkte={kontext?.punkte ?? []} onSchliessen={() => setKontext(null)} />
      <Dialog
        titel="Smart View umbenennen"
        offen={!!umbenennen}
        onSchliessen={() => setUmbenennen(null)}
        breite={440}
        fuss={
          <>
            <button type="button" className="knopf" onClick={() => setUmbenennen(null)}>
              Abbrechen
            </button>
            <button type="button" className="knopf knopf--primaer" disabled={!umbenennen?.name.trim() || umbenennen?.laeuft} onClick={umbenennenSpeichern}>
              {umbenennen?.laeuft ? "Speichert …" : "Umbenennen"}
            </button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            umbenennenSpeichern();
          }}
        >
          <label className="feld">
            <span className="feld__label">Name</span>
            <input className="eingabe" value={umbenennen?.name ?? ""} maxLength={200} onChange={(e) => setUmbenennen((u) => (u ? { ...u, name: e.target.value } : u))} />
          </label>
        </form>
      </Dialog>
      <Dialog
        titel="Smart View löschen?"
        offen={!!loeschen}
        onSchliessen={() => setLoeschen(null)}
        breite={440}
        fuss={
          <>
            <button type="button" className="knopf" onClick={() => setLoeschen(null)}>
              Abbrechen
            </button>
            <button type="button" className="knopf knopf--gefahr" disabled={loeschen?.laeuft} onClick={loeschenBestaetigt}>
              {loeschen?.laeuft ? "Löscht …" : "Löschen"}
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          „{loeschen?.view.name}“ wird gelöscht{loeschen?.view.is_shared ? " und ist dann auch für alle anderen weg" : ""}. Die Leads selbst bleiben unverändert.
        </p>
      </Dialog>
    </div>
  );
}
